import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { TiempoData, TiempoRegistro, ApiResponse } from '../types';
import { getAuthToken } from './AuthContext';

interface TimeTrackingContextType {
    fetchTiempo: (tareaId: number) => Promise<TiempoData | null>;
    iniciar: (tareaId: number, nota?: string) => Promise<boolean>;
    detener: (tareaId: number) => Promise<TiempoRegistro | null>;
    eliminar: (tareaId: number, tiempoId: number) => Promise<boolean>;
}

const TimeTrackingContext = createContext<TimeTrackingContextType | undefined>(undefined);

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

const authFetch = async (path: string, options: RequestInit = {}): Promise<Response> => {
    const token = getAuthToken();
    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...((options.headers as Record<string, string>) || {}),
    };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return fetch(`${API_BASE}${path}`, { ...options, headers });
};

export const TimeTrackingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const fetchTiempo = useCallback(async (tareaId: number): Promise<TiempoData | null> => {
        try {
            const res = await authFetch(`/tareas/${tareaId}/tiempo`);
            const json: ApiResponse<TiempoData> = await res.json();
            if (json.success && json.data) return json.data;
            return null;
        } catch {
            return null;
        }
    }, []);

    const iniciar = useCallback(async (tareaId: number, nota?: string): Promise<boolean> => {
        try {
            const res = await authFetch(`/tareas/${tareaId}/tiempo/iniciar`, {
                method: 'POST',
                body: JSON.stringify({ nota }),
            });
            const json: ApiResponse<TiempoRegistro> = await res.json();
            if (json.success) {
                return true;
            }
            alert(json.error || 'Error al iniciar timer');
            return false;
        } catch {
            return false;
        }
    }, []);

    const detener = useCallback(async (tareaId: number): Promise<TiempoRegistro | null> => {
        try {
            const res = await authFetch(`/tareas/${tareaId}/tiempo/detener`, { method: 'POST' });
            const json: ApiResponse<TiempoRegistro> = await res.json();
            if (json.success && json.data) return json.data;
            return null;
        } catch {
            return null;
        }
    }, []);

    const eliminar = useCallback(async (tareaId: number, tiempoId: number): Promise<boolean> => {
        try {
            const res = await authFetch(`/tareas/${tareaId}/tiempo/${tiempoId}`, { method: 'DELETE' });
            const json: ApiResponse<any> = await res.json();
            return json.success;
        } catch {
            return false;
        }
    }, []);

    return (
        <TimeTrackingContext.Provider value={{ fetchTiempo, iniciar, detener, eliminar }}>
            {children}
        </TimeTrackingContext.Provider>
    );
};

export const useTimeTracking = (): TimeTrackingContextType => {
    const ctx = useContext(TimeTrackingContext);
    if (!ctx) throw new Error('useTimeTracking debe usarse dentro de TimeTrackingProvider');
    return ctx;
};